import { motion } from "framer-motion";
import { Check, ArrowUpRight, Sparkles, Zap } from "lucide-react";

const PLANS = [
  {
    id: "starter",
    name: "Honor Starter Pack",
    tagline: "Tu primer despliegue. Todo lo esencial para empezar a operar con honor.",
    price: "50",
    priceSub: "USD",
    billing: "Mensualidad recurrente",
    cta: "Activar Starter",
    badge: null,
    highlight: false,
    includes: null,
    features: [
      "Clases en vivo",
      "Análisis de mercado AM y PM",
      "Sistema educativo Honor",
      "Biblioteca de contenido grabado",
      "Psicotrading y disciplina",
      "Acceso comunidad básica",
      "Bitácora de progreso",
      "Acceso móvil y web",
      "Seguimiento con IA en tiempo real de la cuenta de trading",
      "Soporte exclusivo de traders FALCONS",
      "Canal de señales exclusivo con indicador Lanza de Neptuno",
      "Ascenso gamer militar de rango",
      "Entrenamientos exclusivos diarios en vivo",
    ],
    footer: "Comienza tu carrera. Primera misión: ON.",
  },
  {
    id: "pro",
    name: "Honor Pro Pack",
    tagline: "Para el operador que quiere acelerar y estar dentro del campo de batalla.",
    price: "75",
    priceSub: "USD",
    billing: "Mensualidad recurrente",
    cta: "Activar Pro",
    badge: "Más popular",
    highlight: true,
    includes: "Honor Starter Pack",
    features: [
      "Trading en vivo",
      "Programa de educación Honor Trading (acceso a las bitácoras de los rangos)",
      "Sesiones operativas en tiempo real",
      "Entradas explicadas en vivo",
      "Gestión de riesgo aplicada",
      "Revisión de operaciones",
      "Sala operativa premium",
      "Mayor cercanía con traders del staff",
      "Arenas o Batallas de trading",
    ],
    footer: "Sala operativa premium y trading en vivo.",
  },
  {
    id: "elite",
    name: "Elite Honor Pack",
    tagline: "El pack del trader profesional. IA, mentorías y herramientas institucionales.",
    price: "95",
    priceSub: "USD",
    billing: "Mensualidad recurrente",
    cta: "Activar Elite",
    badge: "Élite",
    highlight: false,
    pro: true,
    includes: "Honor Pro Pack",
    features: [
      "Clases de futuros",
      "Portafolios comerciales",
      "Herramientas de data analysis",
      "Indicadores premium",
      "Dashboards operativos",
      "Herramientas de seguimiento institucional",
      "Estrategias avanzadas",
      "Posible acceso prioritario a mentorías",
      "IA operativa avanzada",
      "Laboratorio táctico/profesional",
      "Acceso privado a indicadores en TradingView: scripts exclusivos con sistemas validados",
      "Acompañamiento premium y personalizado del STAFF HONOR",
    ],
    footer: "Laboratorio táctico + acompañamiento del STAFF HONOR.",
  },
];

export default function Plans() {
  return (
    <section
      id="planes"
      className="relative py-24 md:py-32 bg-black overflow-hidden"
      data-testid="plans-section"
    >
      <div className="absolute inset-0 radial-green opacity-40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs font-display uppercase tracking-[0.4em] text-[#BEDD7D]">
            / Elige tu misión
          </span>
          <h2 className="mt-6 font-display font-bold text-4xl md:text-6xl uppercase tracking-tight leading-none">
            Packs <span className="italic text-[#BEDD7D]">Honor</span>.
          </h2>
          <p className="mt-6 text-white/60 font-body">
            3 planes mensuales. Sin inscripción. Sube de plan cuando estés listo sin perder progreso.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className={`relative flex flex-col rounded-2xl border transition-all overflow-hidden ${
                plan.highlight
                  ? "bg-gradient-to-b from-[#1A2E1A] to-[#0a0f0a] border-[#BEDD7D] shadow-[0_0_50px_rgba(190,221,125,0.15)] lg:scale-[1.02]"
                  : plan.pro
                  ? "bg-gradient-to-b from-[#0a0f0a] to-black border-[#3E4D36] hover:border-[#BEDD7D]/50"
                  : "bg-[#0a0f0a] border-[#3E4D36]/50 hover:border-[#3E4D36]"
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.badge && (
                <div
                  className={`absolute top-0 right-6 px-3 py-1 text-[10px] font-display font-bold uppercase tracking-[0.25em] rounded-b-lg ${
                    plan.highlight ? "bg-[#BEDD7D] text-black" : "bg-[#3E4D36] text-[#BEDD7D]"
                  }`}
                >
                  {plan.badge}
                </div>
              )}

              <div className="p-7 pt-10">
                <h3 className="font-display font-bold text-2xl md:text-3xl uppercase tracking-tight text-white leading-tight">
                  {plan.name}
                </h3>
                <p className="mt-3 text-sm text-white/60 font-body min-h-[42px]">
                  {plan.tagline}
                </p>

                <div className="mt-7 pb-6 border-b border-[#3E4D36]/40">
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-white/40 text-xl font-bold">$</span>
                    <span className="font-display font-bold text-6xl text-white leading-none tracking-tight">
                      {plan.price}
                    </span>
                    <span className="font-display text-white/50 text-sm uppercase tracking-widest">
                      {plan.priceSub}/mes
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-white/50 font-body">{plan.billing}</div>
                </div>

                <a
                  href="https://app.honor.trading/auth/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group mt-6 inline-flex w-full items-center justify-center gap-2 px-6 py-3.5 font-display font-bold text-xs uppercase tracking-[0.2em] transition-all ${
                    plan.highlight
                      ? "bg-[#BEDD7D] text-black hover:bg-white hover:shadow-[0_0_30px_rgba(190,221,125,0.6)]"
                      : "bg-[#1A2E1A] text-white border border-[#3E4D36] hover:bg-[#3E4D36] hover:border-[#BEDD7D]/60"
                  }`}
                  data-testid={`plan-cta-${plan.id}`}
                >
                  {plan.cta}
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>

              <div className="px-7 pb-7 flex-1">
                {plan.includes && (
                  <div className="flex items-center gap-2 mb-4 pb-4 border-b border-[#3E4D36]/30">
                    <div className="w-6 h-6 rounded-full bg-[#BEDD7D] flex items-center justify-center shrink-0">
                      <Zap className="w-3 h-3 text-black" strokeWidth={3} />
                    </div>
                    <span className="text-sm font-body text-white/90">
                      Incluye TODO el{" "}
                      <span className="font-display font-bold text-[#BEDD7D] uppercase">
                        {plan.includes}
                      </span>{" "}
                      +
                    </span>
                  </div>
                )}

                <div className="text-[11px] font-display uppercase tracking-[0.25em] text-white/40 mb-4">
                  {plan.includes ? "Suma" : "Lo que incluye"}
                </div>

                <ul className="space-y-3">
                  {plan.features.map((f, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3 text-sm text-white/85 font-body leading-relaxed"
                    >
                      <span
                        className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${
                          plan.highlight ? "bg-[#BEDD7D]" : "bg-[#3E4D36]"
                        }`}
                      >
                        <Check
                          className={`w-2.5 h-2.5 ${
                            plan.highlight ? "text-black" : "text-[#BEDD7D]"
                          }`}
                          strokeWidth={3}
                        />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="px-7 py-5 border-t border-[#3E4D36]/40 bg-[#1A2E1A]/20">
                <div className="text-[11px] text-white/60 font-body leading-relaxed mb-2">
                  {plan.footer}
                </div>
                <div className="text-[11px] font-display uppercase tracking-[0.25em] text-[#BEDD7D]">
                  Primera misión: <span className="text-white font-bold">ON</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#BEDD7D]/40 bg-[#1A2E1A]/40 backdrop-blur-md">
            <Sparkles className="w-3.5 h-3.5 text-[#BEDD7D]" />
            <span className="text-[11px] font-display uppercase tracking-widest text-[#BEDD7D]">
              Sin permanencia · Cancela cuando quieras
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
